/** Shared grouped presentation over each surface's existing persisted order. */
import React, { memo, useCallback, useMemo, useState } from "react";
import { moveVisiblePlayer } from "@/features/scouting/hooks/useWatchlistPreferenceOrder";
import type { WatchlistMoveDestination } from "../WatchlistOrderControls/WatchlistOrderControlsContainer";
import PitchMembersListView from "./PitchMembersListView";

/** Surface-owned actions and state remain in their existing containers. */
export interface PitchMembersListRow {
  readonly playerId: string;
  readonly displayName: string;
  readonly position: string;
  readonly gpr: number | null;
  readonly dndId?: string;
  readonly testID: string;
  readonly lineTestID?: string;
  readonly trailing?: React.ReactNode;
  readonly status?: React.ReactNode;
}

/** Inputs shared by watchlist members and scenario additions. */
export interface PitchMembersListProps {
  readonly rows: readonly PitchMembersListRow[];
  readonly title: string;
  readonly count: number;
  readonly canReorder: boolean;
  readonly onReorder?: (ids: readonly string[]) => void;
  readonly onAdd?: () => void;
  readonly footer?: React.ReactNode;
  readonly removalHint?: string;
}

/** A group's local ordinal is presentation only; writes remain a subset of global rank. */
export interface PitchMembersListGroup {
  readonly position: string;
  readonly ids: readonly string[];
  readonly rows: readonly (PitchMembersListRow & {
    readonly rank: number;
    readonly onMove: (destination: WatchlistMoveDestination) => void;
  })[];
}

/** No section open: the initial state (SE-8638). */
const NO_OPEN_POSITIONS: ReadonlySet<string> = new Set();

/**
 * Flips one section, leaving every other section as it was (SE-8650).
 * @param open - The sections open now
 * @param position - The section being toggled
 * @returns The next set of open sections
 */
const togglePosition = (
  open: ReadonlySet<string>,
  position: string
): ReadonlySet<string> =>
  open.has(position)
    ? new Set([...open].filter(other => other !== position))
    : new Set([...open, position]);

/**
 * Binds group-scoped commands to the existing order controller, and owns
 * which position sections are open: every section starts collapsed so all
 * headers fit without scrolling (SE-8638), and any subset can be open at
 * once (SE-8650).
 * @param props - Ordered rows, permissions and surface-owned controls.
 */
const PitchMembersListContainer = (props: PitchMembersListProps) => {
  const { rows, onReorder, canReorder } = props;
  const groups = useMemo<readonly PitchMembersListGroup[]>(
    () =>
      [...new Set(rows.map(row => row.position))]
        .sort((left, right) => left.localeCompare(right))
        .map(position => {
          const members = rows.filter(row => row.position === position);
          const ids = members.map(row => row.playerId);
          return {
            position,
            ids,
            rows: members.map((row, index) => ({
              ...row,
              rank: index + 1,
              onMove: (destination: WatchlistMoveDestination) => {
                if (!canReorder || !onReorder) return;
                const next = moveVisiblePlayer(ids, row.playerId, destination);
                if (next.some((id, i) => id !== ids[i])) onReorder(next);
              },
            })),
          };
        }),
    [rows, onReorder, canReorder]
  );
  // SE-8650: each section owns its own state; toggling one never reads or
  // writes another's. Keyed by position, so a section the formation adds
  // later starts collapsed.
  const [openPositions, setOpenPositions] =
    useState<ReadonlySet<string>>(NO_OPEN_POSITIONS);
  const onToggleGroup = useCallback(
    (position: string) =>
      setOpenPositions(current => togglePosition(current, position)),
    []
  );
  return (
    <PitchMembersListView
      {...props}
      groups={groups}
      openPositions={openPositions}
      onToggleGroup={onToggleGroup}
    />
  );
};
PitchMembersListContainer.displayName = "PitchMembersListContainer";
export default memo(PitchMembersListContainer);
